/**
 * Review List — town filtering without a page load.
 *
 * The chips are real links to `?review_town=<slug>`, and that route works with
 * no JavaScript at all. This only intercepts them, asks core's own
 * `/wp/v2/reviews` collection for the same set, and swaps the cards in. If the
 * request fails the click is handed back to the browser, so the worst case is
 * the page load it would have done anyway.
 *
 * The reviews endpoint already filters by the `town` taxonomy and already
 * carries the ACF values, so there is no custom read route behind this.
 */
(function () {
	'use strict';

	var STAR_FULL = '★';
	var STAR_EMPTY = '☆';

	function esc(text) {
		var d = document.createElement('div');
		d.appendChild(document.createTextNode(text == null ? '' : String(text)));
		return d.innerHTML;
	}

	function stars(rating) {
		var n = Math.max(0, Math.min(5, parseInt(rating, 10) || 0));
		return new Array(n + 1).join(STAR_FULL) + new Array(6 - n).join(STAR_EMPTY);
	}

	/**
	 * One card, matching the markup review-list.php prints for the same review.
	 */
	function card(review, cfg) {
		var acf = review.acf || {};
		var quote = acf.quote || '';
		var name = acf.reviewer_name || '';
		var line = review.source_line || '';
		var url = acf.source_url || '';
		var rating = parseInt(acf.rating, 10) || 0;
		var out = '';

		if (!quote) {
			return '';
		}

		out += '<div class="' + esc(cfg.column) + '">';
		out += '<figure class="' + esc(cfg.item) + ' mb-0">';

		if (cfg.stars === 'yes' && rating) {
			out += '<div class="review-list-stars text-primary fs-5 mb-3" role="img" aria-label="' +
				esc(rating + ' out of 5 stars') + '">' + stars(rating) + '</div>';
		}

		out += '<p class="' + esc(cfg.quote) + '">&ldquo;' + esc(quote) + '&rdquo;</p>';

		if (name || line) {
			out += '<figcaption class="review-list-who text-mute small text-uppercase mt-4">';
			if (name) {
				out += '<cite class="fst-normal">' + esc(name) + '</cite>';
			}
			if (name && line) {
				out += '<span aria-hidden="true"> &middot; </span>';
			}
			if (line && url) {
				out += '<a class="review-list-source-link" href="' + esc(url) +
					'" rel="noopener nofollow" target="_blank">' + esc(line) + '</a>';
			} else if (line) {
				out += esc(line);
			}
			out += '</figcaption>';
		}

		out += '</figure></div>';

		return out;
	}

	function render(results, reviews, cfg) {
		if (!reviews.length) {
			results.innerHTML = '<p class="text-mute mb-0">' + esc(cfg.empty) + '</p>';
			return;
		}

		// Same rule as the server: under three, one centered quote rather than a
		// three-column grid holding a single card.
		var single = reviews.length < 3;
		var grid = single ? 'row justify-content-center g-5' : cfg.grid;
		var shape = single
			? { column: 'col-12 col-lg-8 text-center', item: '', quote: 'review-list-quote fw-bold lh-sm mb-0' }
			: { column: cfg.column, item: cfg.item, quote: cfg.quote };

		var html = '';
		for (var i = 0; i < reviews.length; i++) {
			html += card(reviews[i], {
				column: shape.column,
				item: shape.item,
				quote: shape.quote,
				stars: cfg.stars
			});
		}

		results.innerHTML = '<div class="' + esc(grid) + '">' + html + '</div>';
	}

	function setActive(nav, slug) {
		var chips = nav.querySelectorAll('.review-list-filter');

		for (var i = 0; i < chips.length; i++) {
			var mine = (chips[i].getAttribute('data-town') || '') === slug;
			chips[i].classList.toggle('is-active', mine);

			if (mine) {
				chips[i].setAttribute('aria-current', 'true');
			} else {
				chips[i].removeAttribute('aria-current');
			}
		}
	}

	function init(section) {
		var nav = section.querySelector('.review-list-filters');
		var results = section.querySelector('.review-list-results');

		if (!nav || !results || !window.fetch || results.getAttribute('data-bound')) {
			return;
		}

		results.setAttribute('data-bound', '1');

		var cfg = {
			endpoint: results.getAttribute('data-endpoint'),
			perPage: results.getAttribute('data-per-page') || '12',
			stars: results.getAttribute('data-stars') || 'no',
			grid: results.getAttribute('data-grid') || 'row g-4',
			column: results.getAttribute('data-column') || 'col-md-6 col-lg-4',
			item: results.getAttribute('data-item') || '',
			quote: results.getAttribute('data-quote') || '',
			empty: results.getAttribute('data-empty') || 'No reviews from that town yet.'
		};

		if (!cfg.endpoint) {
			return;
		}

		var inFlight = null;

		function load(slug, term, push) {
			var url = cfg.endpoint + '?per_page=' + encodeURIComponent(cfg.perPage);

			if (term) {
				url += '&town=' + encodeURIComponent(term);
			}

			results.setAttribute('aria-busy', 'true');

			var mine = {};
			inFlight = mine;

			return fetch(url, { headers: { Accept: 'application/json' } })
				.then(function (r) {
					if (!r.ok) {
						throw new Error('HTTP ' + r.status);
					}
					return r.json();
				})
				.then(function (reviews) {
					if (inFlight !== mine) {
						return true; // A later click won.
					}

					render(results, reviews, cfg);
					setActive(nav, slug);
					results.removeAttribute('aria-busy');

					if (push && window.history && window.history.pushState) {
						var here = window.location.pathname;
						window.history.pushState(
							{ reviewTown: slug },
							'',
							slug ? here + '?review_town=' + encodeURIComponent(slug) : here
						);
					}

					return true;
				})
				.catch(function () {
					results.removeAttribute('aria-busy');
					return false;
				});
		}

		nav.addEventListener('click', function (e) {
			var chip = e.target.closest ? e.target.closest('.review-list-filter') : null;

			if (!chip || !nav.contains(chip)) {
				return;
			}

			// Let a modified click do what the reader asked for.
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
				return;
			}

			e.preventDefault();

			var slug = chip.getAttribute('data-town') || '';
			var term = chip.getAttribute('data-term') || '';
			var href = chip.getAttribute('href');

			load(slug, term, true).then(function (ok) {
				if (!ok && href) {
					window.location.href = href; // Fall back to the plain link.
				}
			});
		});

		window.addEventListener('popstate', function () {
			var match = /[?&]review_town=([^&]+)/.exec(window.location.search);
			var slug = match ? decodeURIComponent(match[1]) : '';
			var chip = nav.querySelector('.review-list-filter[data-town="' + slug.replace(/[^a-z0-9-]/gi, '') + '"]');
			load(slug, chip ? chip.getAttribute('data-term') : '', false);
		});
	}

	function initAll(root) {
		var sections = (root || document).querySelectorAll('.wp-block-acf-review-list');

		for (var i = 0; i < sections.length; i++) {
			init(sections[i]);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			initAll(document);
		});
	} else {
		initAll(document);
	}

	if (window.acf) {
		window.acf.addAction('render_block_preview/type=review-list', function ($block) {
			if ($block && $block[0] && $block[0].querySelector('.review-list-results')) {
				setTimeout(function () {
					initAll($block[0]);
				}, 200);
			}
		});
	}
})();
